import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import AppCard from "@/components/base/AppCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import DownloadModal from "@/components/base/DownloadModal";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/base/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { movies, watchApps } from "@/mocks/movies";
import type { WatchApp } from "@/mocks/movies";

const typeOptions = ["全部", "电影", "电视剧", "综艺", "APP"];
const hotKeywords = ["深海回声", "逆光追凶", "长安夜行录", "看片盒子", "星轨之外", "综艺"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState("全部");
  const [downloadApp, setDownloadApp] = useState<WatchApp | null>(null);

  usePageMeta({
    title: keyword ? `“${keyword}”搜索结果 - 看片免费app网站` : "搜索影视与看片APP - 看片免费app网站",
    description: keyword
      ? `在看片免费app网站搜索“${keyword}”，查看相关的电影、电视剧、综艺资源与看片应用推荐。`
      : "搜索电影、电视剧、综艺与看片APP，快速找到想看的影视资源。",
    keywords: keyword
      ? `${keyword},${keyword}搜索,影视资源,看片APP`
      : "影视搜索,电影搜索,电视剧搜索,看片APP搜索",
  });

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.synopsis,
        movie.director,
        movie.region,
        movie.type,
        ...movie.genres,
        ...movie.cast,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [keyword]);

  const appResults = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return watchApps.filter((app) =>
      [app.name, app.summary, app.description, app.category, ...app.platforms, ...app.features]
        .join(" ")
        .toLowerCase()
        .includes(lower)
    );
  }, [keyword]);

  const showMovies = typeFilter === "全部" || ["电影", "电视剧", "综艺"].includes(typeFilter);
  const showApps = typeFilter === "全部" || typeFilter === "APP";
  const visibleMovies = showMovies
    ? movieResults.filter((movie) => typeFilter === "全部" || movie.type === typeFilter)
    : [];
  const visibleApps = showApps ? appResults : [];
  const total = visibleMovies.length + visibleApps.length;

  const runSearch = (value: string) => {
    const trimmed = value.trim();
    setInput(trimmed);
    setTypeFilter("全部");
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    runSearch(input);
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-7 md:py-9">
          <Breadcrumb dark items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 text-2xl md:text-3xl font-bold text-background-50">
            {keyword ? `“${keyword}”的搜索结果` : "搜索影视资源与看片APP"}
          </h1>
          <p className="mt-2 text-sm text-primary-200">
            支持片名、演员、导演、类型与 APP 名称搜索，搜索结果仅覆盖站内已收录内容。
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl rounded-lg sm:rounded-full bg-background-50/95 p-2"
          >
            <div className="flex items-center gap-2 flex-1 px-3">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入关键词，例如：悬疑、韩国、看片盒子"
                aria-label="搜索关键词"
                className="w-full bg-transparent py-2 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-md sm:rounded-full bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 rounded-lg border border-background-200/80 bg-background-50 px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-foreground-500 mr-1">结果类型</span>
            {typeOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setTypeFilter(option)}
                className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  typeFilter === option
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-100 hover:text-primary-600"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          {keyword && (
            <p className="text-xs text-foreground-500">
              共找到 <strong className="text-foreground-900 font-semibold">{total}</strong> 条结果
              <span className="mx-2 text-foreground-300">|</span>
              影视 {visibleMovies.length} 条 · 应用 {visibleApps.length} 条
            </p>
          )}
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        {!keyword ? (
          <div>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-base font-semibold text-foreground-900">试试这些热门关键词</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {hotKeywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => runSearch(word)}
                    className="px-3 py-1.5 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-search-line mr-1"></i>
                    {word}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-900 mb-4">大家都在看</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {movies.slice(0, 5).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        ) : total === 0 ? (
          <>
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与“${keyword}”相关的内容`}
              description="换个片名或更短的关键词试试，也可以从下面的热门内容中挑一部。"
              primaryLabel="返回首页"
              secondaryLabel="浏览热门电影"
              secondaryTo="/category/电影"
            />
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-900">热门推荐</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {movies
                  .filter((movie) => movie.featured)
                  .slice(0, 5)
                  .map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-10">
            {visibleMovies.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground-900">影视结果</h2>
                  <span className="text-xs text-foreground-400">{visibleMovies.length} 条</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {visibleMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </section>
            )}

            {visibleApps.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground-900">应用结果</h2>
                  <span className="text-xs text-foreground-400">{visibleApps.length} 条</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                  {visibleApps.map((app) => (
                    <AppCard key={app.id} app={app} onDownload={setDownloadApp} />
                  ))}
                </div>
              </section>
            )}

            {total > 0 && (
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-secondary-900">
                  没找到想要的？直接浏览分类页，筛选条件更细致。
                </p>
                <div className="flex flex-wrap gap-2">
                  {["电影", "电视剧", "综艺"].map((type) => (
                    <Link
                      key={type}
                      to={`/category/${type}`}
                      className="px-3 py-1.5 rounded-md bg-background-50 text-xs text-foreground-700 hover:text-primary-600 border border-background-200"
                    >
                      {type}频道
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <DownloadModal
        open={Boolean(downloadApp)}
        appName={downloadApp?.name ?? ""}
        onClose={() => setDownloadApp(null)}
      />
    </SiteLayout>
  );
}